import { TModalCustomView, Loader } from '@ws/shared/components';
import { getErrorMessage } from '@ws/shared/utils';
import { classes } from 'classifizer';
import mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

import styles from './UploadFileModal.module.css';

function splitByLastDot(str: string) {
  const lastDotIndex = str.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return [str, ''];
  }

  const leftPart = str.substring(0, lastDotIndex);
  const rightPart = str.substring(lastDotIndex + 1).toLowerCase();
  return [leftPart, rightPart];
}

const modalTitle = 'Загрузить файл';

export function showUploadFileModal(
  processHtmlContent: (name: string, htmlContent: string) => void,
  processJsonContent: (jsonContent: string) => void,
): TModalCustomView {
  return function UploadFileModal({
    closeAllModals,
    onAwayClose,
    components: { ModalWrapper, ModalContentWrapper, ModalCloseButton, ModalTitle, ModalContent },
  }) {
    const [isLoading, setIsLoading] = useState(false);

    const onDrop = useCallback(
      async (acceptedFiles: File[]) => {
        setIsLoading(true);

        const file = acceptedFiles[0];
        const [name, ext] = splitByLastDot(file.name);

        try {
          if (ext === 'txt') {
            // Plain text
            const text = await file.text();
            const htmlContent = `<p>${text
              .split(/\r?\n/)
              .map((line) => line.trim())
              .filter((line) => line)
              .join('</p><p>')}</p>`;

            processHtmlContent(name, htmlContent);

            closeAllModals();
            return;
          }

          if (ext === 'docx') {
            // DOCX to HTML via Mammoth
            const arrayBuffer = await file.arrayBuffer();
            const { value: htmlContent } = await mammoth.convertToHtml(
              { arrayBuffer },
              {
                styleMap: [
                  "p[style-name='Heading 1'] => h1:fresh",
                  "p[style-name='Heading 2'] => h2:fresh",
                  'b => strong',
                  'i => em',
                ],
              },
            );

            processHtmlContent(name, htmlContent);

            closeAllModals();
            return;
          }

          if (ext === 'pdf') {
            const arrayBuffer = await file.arrayBuffer();
            const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

            let htmlContent = '';
            for (let i = 1; i <= pdf.numPages; i++) {
              const page = await pdf.getPage(i);
              const textContent = await page.getTextContent();
              const pageText = textContent.items.map((item: any) => item.str).join(' ');
              htmlContent += `<p>${pageText}</p>`;
            }

            processHtmlContent(name, htmlContent);

            closeAllModals();
            return;
          }

          if (ext === 'json') {
            const plainText = await file.text();

            try {
              const parsed = JSON.parse(plainText);
              processJsonContent(parsed);
            } catch (err) {
              console.error(err);

              if (err instanceof SyntaxError) {
                toast.error('Bad file to parse. Choose another one.');
                return;
              }

              toast.error(getErrorMessage(err));
            }

            closeAllModals();
            return;
          }

          console.error('Unsupported file format:', ext);

          toast.error(`Can't process this file format: ${ext}. Please, try another one.`);
          return;
        } catch (error) {
          console.error('Error parsing file:', error);
        } finally {
          setIsLoading(false);
        }
      },
      [closeAllModals],
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
      accept: {
        'text/plain': ['.txt', '.json'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
        'application/pdf': ['.pdf'],
      },
      multiple: false,
    });

    return (
      <ModalWrapper onClick={onAwayClose}>
        <ModalContentWrapper>
          <ModalCloseButton onClose={closeAllModals} />
          <ModalTitle>{modalTitle}</ModalTitle>
          <ModalContent>
            <div
              {...getRootProps()}
              className={classes(styles['dropzone'], isDragActive && styles['dropzone_active'])}
            >
              <input {...getInputProps()} />

              {isLoading ? (
                <Loader />
              ) : isDragActive ? (
                <p>Drop the file here...</p>
              ) : (
                <p>
                  Перетащите сюда файл в формате TXT, DOCX, PDF
                  <br />
                  или JSON, который вы ранее скачали из Textator,
                  <br />
                  или нажмите, чтобы выбрать его.
                </p>
              )}
            </div>
          </ModalContent>
        </ModalContentWrapper>
      </ModalWrapper>
    );
  };
}
